import { useCallback, RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { SceneInfo, SceneWithUserNames, UpdateSceneParams, CreateSceneParams } from '../../../../types/scene.types';
import { useSceneUpdates as useSceneUpdatesHook } from '../../../../hooks/dataUpdates/useSceneUpdates';
import { SceneDataStatus } from '../../../../types/scene.types';
import { getCurrentUserInfo } from '../../../../utils/userUtils';
import { useDataCache } from '../../../../contexts/BreakdownDataCacheContext';

// 判断是否是占位行（临时 ID）
const isPlaceholderScene = (sceneId: string): boolean => {
  return sceneId.startsWith('temp-');
};

// 从 SceneInfo 中提取 CreateSceneParams 允许的字段
const extractCreateSceneParams = (scene: SceneWithUserNames | undefined): Partial<CreateSceneParams> => {
  if (!scene) return {};
  
  // 只提取 CreateSceneParams 中定义的字段
  const params: Partial<CreateSceneParams> = {};
  
  if (scene.sceneCode !== undefined) params.sceneCode = scene.sceneCode;
  if (scene.sceneName !== undefined) params.sceneName = scene.sceneName;
  if (scene.sceneFilmID !== undefined) params.sceneFilmID = scene.sceneFilmID;
  if (scene.sceneBelongBreakdownID !== undefined) params.sceneBelongBreakdownID = scene.sceneBelongBreakdownID;
  if (scene.sceneCreatorID !== undefined) params.sceneCreatorID = scene.sceneCreatorID;
  if (scene.sceneDataStatus !== undefined) params.sceneDataStatus = scene.sceneDataStatus;
  if (scene.scenePeriod !== undefined) params.scenePeriod = scene.scenePeriod;
  if (scene.sceneBudgetAmount !== undefined) params.sceneBudgetAmount = scene.sceneBudgetAmount;
  if (scene.scenePlot !== undefined) params.scenePlot = scene.scenePlot;
  if (scene.scenePlotSummary !== undefined) params.scenePlotSummary = scene.scenePlotSummary;
  if (scene.scenePlotDay !== undefined) params.scenePlotDay = scene.scenePlotDay;
  if (scene.scenePage !== undefined) params.scenePage = scene.scenePage;
  if (scene.scenePagesNumber !== undefined) params.scenePagesNumber = scene.scenePagesNumber;
  if (scene.sceneLocation !== undefined) params.sceneLocation = scene.sceneLocation;
  if (scene.sceneSet !== undefined) params.sceneSet = scene.sceneSet;
  if (scene.sceneInteriorExterior !== undefined) params.sceneInteriorExterior = scene.sceneInteriorExterior;
  if (scene.sceneDayNight !== undefined) params.sceneDayNight = scene.sceneDayNight;
  
  return params;
};

/**
 * 场次更新逻辑 Hook
 * 统一管理场次数据的更新逻辑，只在完成编辑时保存
 */
export const useSceneUpdates = (
  scenes: SceneWithUserNames[],
  selectedSceneId: string | null,
  setScenes: React.Dispatch<React.SetStateAction<SceneWithUserNames[]>>,
  setSelectedSceneData: React.Dispatch<React.SetStateAction<Partial<SceneInfo> | undefined>>,
  pendingSceneUpdatesRef: RefObject<Map<string, Partial<UpdateSceneParams>>>,
  updateTimersRef: RefObject<Map<string, number>>,
  createSceneData?: (breakdownId: string, data: Partial<SceneInfo>) => Promise<string>,
  setSelectedSceneId?: React.Dispatch<React.SetStateAction<string | null>>,
  onAfterFlushToCache?: () => void,
) => {
  const { t } = useTranslation();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { createSceneData: createSceneDataFromHook } = useSceneUpdatesHook();
  const { getBreakdownCache, setBreakdownCache, setSceneCache } = useDataCache();

  /**
   * 处理场次数据更新（保存到待提交队列，并乐观更新 breakdown 状态）
   */
  const handleSceneUpdate = useCallback(async (sceneId: string, field: string, value: any) => {
    try {
      // 对于内外、日夜字段，只做乐观更新，不存储到待提交队列
      // 这些字段只在完成选择时（onCellEditEnd）才更新缓存
      if (field === 'sceneInteriorExterior' || field === 'sceneDayNight') {
        // 只做乐观更新，确保 UI 立即响应
        setScenes(prev => {
          const oldScene = prev.find(s => s.sceneID === sceneId);
          const oldValue = oldScene ? (oldScene as any)[field] : undefined;
          console.log(`[下拉选择] sceneID: ${sceneId}, 字段: ${field}, 原值: ${oldValue}, 新值: ${value}`);
          return prev.map(scene => 
            scene.sceneID === sceneId 
              ? { ...scene, [field]: value } as SceneWithUserNames
              : scene
          );
        });
        return;
      }

      // 其他字段：合并到待提交的变更集中（按场次ID聚合多个字段）
      if (!pendingSceneUpdatesRef.current) {
        return;
      }

      const pendingMap = pendingSceneUpdatesRef.current;
      const existedPayload = pendingMap.get(sceneId) || {};
      (existedPayload as any)[field] = value;
      pendingMap.set(sceneId, existedPayload);

      // 乐观更新 breakdown 状态，确保 UI 立即响应
      // 注意：这里不更新缓存，缓存只在完成编辑时更新
      setScenes(prev => prev.map(scene => 
        scene.sceneID === sceneId 
          ? { ...scene, [field]: value } as SceneWithUserNames
          : scene
      ));

      // 若正在编辑当前选中场次，同步更新 selectedSceneData，避免切换场次后详情页显示旧数据
      if (sceneId === selectedSceneId) {
        setSelectedSceneData(prev => ({ ...prev, [field]: value }));
      }
    } catch (error) {
      console.error(t('breakdown.updateSceneFailed'), error);
    }
  }, [pendingSceneUpdatesRef, setScenes, setSelectedSceneData, selectedSceneId, t]);

  /**
   * 完成编辑时提交某个场次的待提交更新
   * 只负责更新缓存，由缓存自动触发 API 请求
   * @param sceneId 场次ID
   * @param fieldValue 可选：字段的新值（用于内外、日夜字段，确保使用最新值）
   * @param fieldName 可选：字段名称（用于内外、日夜字段）
   */
  const flushSceneUpdate = useCallback(async (sceneId: string, fieldValue?: any, fieldName?: string) => {
    // 获取 breakdownId
    const scene = scenes.find(s => s.sceneID === sceneId);
    const breakdownId = scene?.sceneBelongBreakdownID;
    
    // 检查是否是内外、日夜、场景、拍摄地点字段的更新
    // 如果传入了 fieldValue 和 fieldName，说明是这些字段
    if (fieldName && (fieldName === 'sceneInteriorExterior' || fieldName === 'sceneDayNight' || fieldName === 'sceneSet' || fieldName === 'sceneLocation')) {
      // 内外、日夜、场景、拍摄地点字段：使用传入的新值（确保是最新的）
      const newValue = fieldValue;
      
      // 获取缓存中的旧值
      const cache = breakdownId ? getBreakdownCache(breakdownId) : null;
      const cachedScene = cache?.scenes.find(s => s.sceneID === sceneId);
      const oldValue = cachedScene ? (cachedScene as any)[fieldName] : undefined;
      
      // 如果新值和旧值相同，跳过更新（避免不必要的更新）
      if (oldValue === newValue) {
        return;
      }
      // 先做乐观更新 breakdown 状态，确保 UI 立即响应
      setScenes(prev => prev.map(s => 
        s.sceneID === sceneId ? { ...s, [fieldName]: newValue } as SceneWithUserNames : s
      ));
      
      if (breakdownId && cache) {
        const updatedScene = cache.scenes.find(s => s.sceneID === sceneId);
        const mergedScene = updatedScene ? { ...updatedScene, [fieldName]: newValue } : null;
        const updatedScenes = cache.scenes.map((s) =>
          s.sceneID === sceneId ? (mergedScene || { ...s, [fieldName]: newValue }) : s
        );
        setBreakdownCache(breakdownId, {
          scenes: updatedScenes,
        });
        if (mergedScene) {
          setSceneCache(sceneId, { sceneInfo: mergedScene as SceneInfo });
        }
      }
      onAfterFlushToCache?.();
      return;
    }
    
    // 其他字段：从待提交队列获取
    const payload = pendingSceneUpdatesRef.current?.get(sceneId);
    
    // 如果是占位行且没有任何输入，删除占位行
    if (isPlaceholderScene(sceneId)) {
      if (!payload || Object.keys(payload).length === 0) {
        // 没有任何输入，删除占位行
        setScenes(prev => prev.filter(s => s.sceneID !== sceneId));
        if (sceneId === selectedSceneId && setSelectedSceneId) {
          setSelectedSceneId(null);
        }
        pendingSceneUpdatesRef.current?.delete(sceneId);
        return;
      }
    }
    
    if (!payload || Object.keys(payload).length === 0) return;
    
    try {
      // 如果是占位行，需要先创建真实数据
      if (isPlaceholderScene(sceneId) && createSceneData && breakdownId) {
        const userInfo = getCurrentUserInfo();
        // 提取占位行中符合 CreateSceneParams 的字段
        const sceneParams = extractCreateSceneParams(scene);
        
        // 创建真实场次（这会调用 API 并更新缓存）
        const realSceneId = await createSceneData(breakdownId, {
          ...sceneParams,
          ...payload,
          sceneBelongBreakdownID: breakdownId,
          sceneCreatorID: scene?.sceneCreatorID || userInfo.userID,
          sceneDataStatus: scene?.sceneDataStatus || SceneDataStatus.NORMAL,
        });
        
        // 等待一下，确保缓存更新完成
        await new Promise(resolve => setTimeout(resolve, 10));
        
        // 从缓存获取真实数据（createSceneData 已经更新了缓存）
        const cache = getBreakdownCache(breakdownId);
        const realScene = cache?.scenes.find(s => s.sceneID === realSceneId);
        
        // 用真实 ID 替换临时 ID，并在更新完成后选中新行
        setScenes(prev => {
          const updated = prev.map(s => 
            s.sceneID === sceneId 
              ? { ...s, sceneID: realSceneId, ...payload } as SceneWithUserNames
              : s
          );
          // 如果更新了场号，需要重新排序
          if (payload.sceneCode !== undefined) {
            const { sortScenes } = require('../Utils/SceneCodeUtils');
            const sorted = sortScenes(updated, 'sceneCode', 'asc');
            // 排序后，确保新创建的行在数组中，然后选中
            setTimeout(() => {
              if (setSelectedSceneId) {
                setSelectedSceneId(realSceneId);
                // 从缓存获取真实数据并更新 selectedSceneData
                if (realScene) {
                  setSelectedSceneData(realScene as Partial<SceneInfo>);
                } else {
                  setSelectedSceneData(prev => {
                    const updated = { ...prev, sceneID: realSceneId, ...payload };
                    return updated as Partial<SceneInfo>;
                  });
                }
              }
            }, 100);
            return sorted;
          }
          // 如果没有排序，立即选中
          setTimeout(() => {
            if (setSelectedSceneId) {
              setSelectedSceneId(realSceneId);
              // 从缓存获取真实数据并更新 selectedSceneData
              if (realScene) {
                setSelectedSceneData(realScene as Partial<SceneInfo>);
              } else {
                setSelectedSceneData(prev => {
                  const updated = { ...prev, sceneID: realSceneId, ...payload };
                  return updated as Partial<SceneInfo>;
                });
              }
            }
          }, 100);
          return updated;
        });
        
        // 清理占位行的待提交数据
        pendingSceneUpdatesRef.current?.delete(sceneId);
        onAfterFlushToCache?.();
      } else {
        // 正常更新已存在的场次
        // 只更新缓存，由缓存自动触发 API
        if (breakdownId) {
          // 使用最新的 breakdown 状态（包含乐观更新）来更新缓存
          // 合并 payload，确保同帧内添加的字段（如 scenePlotSummary 自动填充）也能写入
          const optimisticScene = scenes.find(s => s.sceneID === sceneId);
          const cache = getBreakdownCache(breakdownId);
          const sceneToFlush = optimisticScene ? { ...optimisticScene, ...payload } : null;
          if (cache && sceneToFlush) {
            const updatedScenes = cache.scenes.map((scene) =>
              scene.sceneID === sceneId ? sceneToFlush : scene
            );
            setBreakdownCache(breakdownId, {
              scenes: updatedScenes,
            });
            // 同步更新 Scene 缓存，确保切换场次再切回时详情页显示最新数据
            setSceneCache(sceneId, { sceneInfo: sceneToFlush as SceneInfo });
          }
        }
        pendingSceneUpdatesRef.current?.delete(sceneId);
        onAfterFlushToCache?.();
      }
    } catch (err) {
      console.error(t('breakdown.updateSceneFailed'), err);
      // 如果是占位行创建失败，删除占位行
      if (isPlaceholderScene(sceneId)) {
        setScenes(prev => prev.filter(s => s.sceneID !== sceneId));
        if (sceneId === selectedSceneId && setSelectedSceneId) {
          setSelectedSceneId(null);
        }
      }
      pendingSceneUpdatesRef.current?.delete(sceneId);
      throw err; // 重新抛出错误，让调用者知道失败
    }
  }, [scenes, selectedSceneId, setScenes, setSelectedSceneId, setSelectedSceneData, pendingSceneUpdatesRef, createSceneData, getBreakdownCache, setBreakdownCache, setSceneCache, t, onAfterFlushToCache]);

  return {
    handleSceneUpdate,
    flushSceneUpdate,
  };
};
